import React, { useEffect, useState } from 'react';
import axios from '../api/axios';
import assetUrl from '../api/assetUrl';
import { toFormData } from '../api/formData';
import MultilingualField from '../components/MultilingualField';
import { useNotify, errorMessage } from '../components/Toast';
import { IconEdit, IconTrash } from '../components/Icons';
import '../components/AdminUI.css';

const emptyPost = () => ({
  slug: '',
  category_id: '',
  author_name: '',
  cover_image: '',
  i18n: { fr: { title: '', excerpt: '', content: '' }, ar: { title: '', excerpt: '', content: '' }, en: { title: '', excerpt: '', content: '' } },
  tags: [],
  is_published: false,
  is_featured: false,
  published_at: '',
  meta_title: '',
  meta_description: '',
});

const emptyCategory = () => ({ slug: '', name: '', i18n: {}, is_active: true, sort_order: 0 });

export default function Blog() {
  const [tab, setTab] = useState('posts');
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [editingCat, setEditingCat] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const notify = useNotify();

  const loadPosts = () => axios.get('/blog/posts?per_page=100').then((r) => setPosts(r.data.data || r.data));
  const loadCategories = () => axios.get('/blog/categories').then((r) => setCategories(r.data));

  useEffect(() => { loadPosts(); loadCategories(); }, []);

  // ---------- Posts ----------
  const savePost = async () => {
    try {
      const payload = { ...editingPost };
      // category_id empty string -> null
      if (payload.category_id === '' || payload.category_id == null) delete payload.category_id;
      // strings for JSON fields when going through FormData
      const fd = toFormData({
        ...payload,
        i18n: JSON.stringify(payload.i18n || {}),
        tags: JSON.stringify(payload.tags || []),
      });
      const url = editingPost.id ? `/blog/posts/${editingPost.id}` : '/blog/posts';
      await axios.post(url, fd);
      notify.success(editingPost.id ? 'Article mis à jour' : 'Article créé');
      setEditingPost(null);
      loadPosts();
    } catch (err) {
      notify.error(errorMessage(err, "Erreur lors de l'enregistrement de l'article"));
    }
  };

  const removePost = async (id) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    await axios.delete(`/blog/posts/${id}`);
    loadPosts();
  };

  const editPost = (p) => {
    setEditingPost({
      ...emptyPost(),
      ...p,
      category_id: p.category_id ?? '',
      i18n: { ...emptyPost().i18n, ...(p.i18n || {}) },
      tags: p.tags || [],
      published_at: p.published_at ? p.published_at.substring(0, 16) : '',
    });
    setTab('posts');
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    setEditingPost({ ...editingPost, tags: [...(editingPost.tags || []), t] });
    setTagInput('');
  };

  // ---------- Categories ----------
  const saveCategory = async () => {
    try {
      const url = editingCat.id ? `/blog/categories/${editingCat.id}` : '/blog/categories';
      const method = editingCat.id ? 'put' : 'post';
      await axios[method](url, editingCat);
      notify.success(editingCat.id ? 'Catégorie mise à jour' : 'Catégorie créée');
      setEditingCat(null);
      loadCategories();
    } catch (err) {
      notify.error(errorMessage(err, "Erreur lors de l'enregistrement de la catégorie"));
    }
  };
  const removeCategory = async (id) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    await axios.delete(`/blog/categories/${id}`);
    loadCategories();
  };

  return (
    <div className="admin-page">
      <div className="admin-toolbar" style={{ justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={`admin-btn ${tab === 'posts' ? 'admin-btn-gold' : 'admin-btn-outline'}`} onClick={() => setTab('posts')}>Articles</button>
          <button className={`admin-btn ${tab === 'categories' ? 'admin-btn-gold' : 'admin-btn-outline'}`} onClick={() => setTab('categories')}>Catégories</button>
        </div>
        {tab === 'posts' && !editingPost && (
          <button className="admin-btn admin-btn-gold" onClick={() => setEditingPost(emptyPost())}>+ Nouvel article</button>
        )}
        {tab === 'categories' && !editingCat && (
          <button className="admin-btn admin-btn-gold" onClick={() => setEditingCat(emptyCategory())}>+ Nouvelle catégorie</button>
        )}
      </div>

      {tab === 'posts' && editingPost && (
        <div className="admin-form-card">
          <div className="admin-form-header">
            <h3>{editingPost.id ? "Modifier l'article" : 'Nouvel article'}</h3>
          </div>
          <div className="admin-form-body">
            <div className="admin-form-section">
              <div className="admin-form-section-title">Image de couverture</div>
              <input type="file" accept="image/*" onChange={(e) => setEditingPost({ ...editingPost, cover_file: e.target.files?.[0] })} />
              {editingPost.cover_image && (
                <img src={assetUrl(editingPost.cover_image)} alt="" style={{ height: 120, marginTop: 8, borderRadius: 4, border: '1px solid #e5e7eb' }} />
              )}
            </div>

            <div className="admin-form-section">
              <div className="admin-form-section-title">Général</div>
              <div className="admin-grid admin-grid-2">
                <div>
                  <label className="admin-label">Slug (laisser vide pour générer)</label>
                  <input className="admin-input" value={editingPost.slug || ''} onChange={(e) => setEditingPost({ ...editingPost, slug: e.target.value })} />
                </div>
                <div>
                  <label className="admin-label">Catégorie</label>
                  <select className="admin-input" value={editingPost.category_id || ''} onChange={(e) => setEditingPost({ ...editingPost, category_id: e.target.value })}>
                    <option value="">— Aucune —</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="admin-label">Auteur</label>
                  <input className="admin-input" value={editingPost.author_name || ''} onChange={(e) => setEditingPost({ ...editingPost, author_name: e.target.value })} />
                </div>
                <div>
                  <label className="admin-label">Date de publication</label>
                  <input className="admin-input" type="datetime-local" value={editingPost.published_at || ''} onChange={(e) => setEditingPost({ ...editingPost, published_at: e.target.value })} />
                </div>
                <div>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: '#475569' }}>
                    <input type="checkbox" checked={!!editingPost.is_published} onChange={(e) => setEditingPost({ ...editingPost, is_published: e.target.checked })} /> Publié
                  </label>
                </div>
                <div>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: '#475569' }}>
                    <input type="checkbox" checked={!!editingPost.is_featured} onChange={(e) => setEditingPost({ ...editingPost, is_featured: e.target.checked })} /> En vedette
                  </label>
                </div>
              </div>
            </div>

            <MultilingualField
              label="Contenu"
              value={editingPost.i18n}
              fields={['title', 'excerpt', 'content']}
              multiline={['excerpt', 'content']}
              onChange={(v) => setEditingPost({ ...editingPost, i18n: v })}
            />

            <div className="admin-form-section">
              <div className="admin-form-section-title">Tags</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="admin-input" placeholder="Ajouter un tag…" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} />
                <button type="button" className="admin-btn admin-btn-outline" onClick={addTag}>Ajouter</button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {(editingPost.tags || []).map((t, i) => (
                  <span key={i} style={{ background: '#f1f5f9', color: '#334155', padding: '4px 10px', borderRadius: 999, fontSize: 12 }}>
                    {t}
                    <button type="button" onClick={() => setEditingPost({ ...editingPost, tags: editingPost.tags.filter((_, idx) => idx !== i) })} style={{ marginLeft: 6, border: 'none', background: 'transparent', cursor: 'pointer', color: '#64748b' }}>×</button>
                  </span>
                ))}
              </div>
            </div>

            <div className="admin-form-section">
              <div className="admin-form-section-title">SEO</div>
              <div className="admin-grid admin-grid-1">
                <div>
                  <label className="admin-label">Meta title</label>
                  <input className="admin-input" value={editingPost.meta_title || ''} onChange={(e) => setEditingPost({ ...editingPost, meta_title: e.target.value })} />
                </div>
                <div>
                  <label className="admin-label">Meta description</label>
                  <textarea className="admin-textarea" rows={2} value={editingPost.meta_description || ''} onChange={(e) => setEditingPost({ ...editingPost, meta_description: e.target.value })} />
                </div>
              </div>
            </div>
          </div>
          <div className="admin-form-actions">
            <button className="admin-btn admin-btn-outline" onClick={() => setEditingPost(null)}>Annuler</button>
            <button className="admin-btn admin-btn-gold" onClick={savePost}>Enregistrer</button>
          </div>
        </div>
      )}

      {tab === 'posts' && !editingPost && (
        <div className="admin-grid admin-grid-3">
          {posts.map((p) => (
            <div key={p.id} className="admin-card" style={{ padding: 12 }}>
              {p.cover_image && <img src={assetUrl(p.cover_image)} alt="" style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 4 }} />}
              <div style={{ marginTop: 8, fontWeight: 600 }}>{p.i18n?.fr?.title || p.slug || '(sans titre)'}</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                <span className={`status-badge ${p.is_published ? 'active' : 'inactive'}`}>{p.is_published ? 'Publié' : 'Brouillon'}</span>
                {p.is_featured && <span style={{ marginLeft: 6, background: '#fef3c7', color: '#b45309', padding: '2px 8px', borderRadius: 999, fontSize: 11 }}>Vedette</span>}
                {p.category && <span style={{ marginLeft: 6 }}>{p.category.name}</span>}
              </div>
              <div className="admin-actions" style={{ marginTop: 10 }}>
                <button className="admin-icon-btn" title="Modifier" onClick={() => editPost(p)}><IconEdit /></button>
                <button className="admin-icon-btn is-danger" title="Supprimer" onClick={() => removePost(p.id)}><IconTrash /></button>
              </div>
            </div>
          ))}
          {posts.length === 0 && <div style={{ padding: 24, color: '#64748b' }}>Aucun article pour le moment.</div>}
        </div>
      )}

      {tab === 'categories' && editingCat && (
        <div className="admin-form-card">
          <div className="admin-form-header">
            <h3>{editingCat.id ? 'Modifier la catégorie' : 'Nouvelle catégorie'}</h3>
          </div>
          <div className="admin-form-body">
            <div className="admin-grid admin-grid-2">
              <div>
                <label className="admin-label">Nom (FR)</label>
                <input className="admin-input" value={editingCat.name || ''} onChange={(e) => setEditingCat({ ...editingCat, name: e.target.value })} />
              </div>
              <div>
                <label className="admin-label">Slug (auto si vide)</label>
                <input className="admin-input" value={editingCat.slug || ''} onChange={(e) => setEditingCat({ ...editingCat, slug: e.target.value })} />
              </div>
              <div>
                <label className="admin-label">Ordre</label>
                <input className="admin-input" type="number" value={editingCat.sort_order || 0} onChange={(e) => setEditingCat({ ...editingCat, sort_order: parseInt(e.target.value || 0) })} />
              </div>
              <div>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: '#475569' }}>
                  <input type="checkbox" checked={!!editingCat.is_active} onChange={(e) => setEditingCat({ ...editingCat, is_active: e.target.checked })} /> Active
                </label>
              </div>
            </div>
            <MultilingualField label="Traductions" value={editingCat.i18n} fields={['name']} onChange={(v) => setEditingCat({ ...editingCat, i18n: v })} />
          </div>
          <div className="admin-form-actions">
            <button className="admin-btn admin-btn-outline" onClick={() => setEditingCat(null)}>Annuler</button>
            <button className="admin-btn admin-btn-gold" onClick={saveCategory}>Enregistrer</button>
          </div>
        </div>
      )}

      {tab === 'categories' && !editingCat && (
        <div className="admin-grid admin-grid-3">
          {categories.map((c) => (
            <div key={c.id} className="admin-card" style={{ padding: 12 }}>
              <div style={{ fontWeight: 600 }}>{c.i18n?.fr?.name || c.name}</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                <span className={`status-badge ${c.is_active ? 'active' : 'inactive'}`}>{c.is_active ? 'Active' : 'Inactive'}</span>
                <span style={{ marginLeft: 8 }}>/{c.slug}</span>
              </div>
              <div className="admin-actions" style={{ marginTop: 10 }}>
                <button className="admin-icon-btn" title="Modifier" onClick={() => setEditingCat(c)}><IconEdit /></button>
                <button className="admin-icon-btn is-danger" title="Supprimer" onClick={() => removeCategory(c.id)}><IconTrash /></button>
              </div>
            </div>
          ))}
          {categories.length === 0 && <div style={{ padding: 24, color: '#64748b' }}>Aucune catégorie.</div>}
        </div>
      )}
    </div>
  );
}
